/**
 * TransactionBulkRegisterPage (거래 일괄 입력 화면, A-7)
 *
 * @why 단건 등록은 저장할 때마다 홈으로 이동해 목록 전체를 다시 읽는다.
 * 여러 건을 넣으려면 그 왕복을 건수만큼 반복해야 해서 느리다.
 * 이 화면은 입력하는 동안 화면에 머물고, 저장과 목록 재조회를 마지막에 한 번만 한다.
 *
 * 구조 원칙:
 * - 카테고리/결제수단/멤버 조회는 **이 페이지에서 한 번만** 한다.
 *   행 컴포넌트에서 각자 조회하면 최대 50배의 구독과 재렌더가 생긴다.
 * - 행 컴포넌트는 상태를 갖지 않는 표현 전용이고, 표현(테이블/카드)만 화면 폭에 따라 갈린다.
 */

import { useCallback, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Button } from '@/shared/components/ui/Button'
import { ErrorDialog } from '@/shared/components/ui/ErrorDialog'
import { useMediaQuery } from '@/shared/hooks/useMediaQuery'
import { STORAGE_KEYS } from '@/core/config/constants'
import { useWorkspaceStore } from '@/core/workspace/workspaceStore'
import { useCategories } from '@/domains/category/hooks/useCategories'
import { usePaymentMethods } from '@/domains/payment/hooks/usePaymentMethods'
import { useUsers } from '@/domains/user/hooks/useUsers'
import { useCurrentUser } from '@/domains/user/hooks/useCurrentUser'
import { BulkTransactionTable } from '../components/BulkTransactionTable'
import { BulkTransactionRowCard } from '../components/BulkTransactionRowCard'
import {
  useBulkTransactionDraft,
  DEFAULT_MAX_ROWS,
  ROW_COUNT_WARNING_THRESHOLD,
} from '../hooks/useBulkTransactionDraft'
import {
  useCreateTransactionsBulk,
  type BulkSaveFailure,
} from '../hooks/useCreateTransactionsBulk'
import {
  validateAll,
  type BulkValidationContext,
  type BulkValidationSummary,
} from '../utils/bulkTransactionValidator'
import { loadBulkValidationContext } from '../utils/loadBulkValidationContext'
import type {
  BulkRowViewProps,
  BulkTransactionDraftRow,
} from '../types/BulkTransaction'

/** 이 폭 이상이면 테이블, 미만이면 카드 */
const WIDE_SCREEN_QUERY = '(min-width: 768px)'

export function TransactionBulkRegisterPage() {
  const navigate = useNavigate()
  const isWideScreen = useMediaQuery(WIDE_SCREEN_QUERY)
  const activeWorkspaceId = useWorkspaceStore((state) => state.activeWorkspaceId)

  // 마스터 데이터: 이 페이지에서 한 번만 조회해 모든 행이 공유한다
  const {
    expenseCategories,
    incomeCategories,
    loading: categoriesLoading,
  } = useCategories()
  const { paymentMethods, loading: paymentMethodsLoading } = usePaymentMethods()
  const { users, loading: usersLoading } = useUsers()
  const { currentUser } = useCurrentUser()

  const isDataLoading = categoriesLoading || paymentMethodsLoading || usersLoading

  /**
   * 작성 중인 내용을 워크스페이스별로 보관한다.
   *
   * @why 워크스페이스마다 카테고리·결제수단·멤버가 다르다. 키를 공유하면
   * 다른 가계부에서 쓰던 드래프트가 되살아나 참조가 전부 어긋난 행이 뜬다.
   */
  const draftStorageKey = `${STORAGE_KEYS.BULK_TRANSACTION_DRAFT_PREFIX}:${activeWorkspaceId ?? 'default'}`

  const { rows, addRow, updateRow, removeRow, canAddRow, clearDraft } =
    useBulkTransactionDraft({
      paymentMethods,
      defaultSpenderId: currentUser?.id ?? '',
      storageKey: draftStorageKey,
    })

  const { saveAll, loading: saving } = useCreateTransactionsBulk()

  /**
   * 저장 실패 상세. 값이 있으면 모달을 띄운다.
   *
   * @why 훅의 error 문자열을 그대로 쓰지 않고 실패 객체를 보관하는 이유:
   * 어느 단계에서 끊겼는지·중복 저장 위험이 있는지·진단 정보가 무엇인지까지
   * 모달이 한 번에 보여줘야 한다. 로그를 볼 수 없는 기기에서는 이 화면이
   * 유일한 관측 창구다.
   */
  const [failureDialog, setFailureDialog] = useState<BulkSaveFailure | null>(null)

  /** 검증으로 저장이 막힌 오류 행 수. null이면 모달을 닫은 상태 */
  const [validationBlocked, setValidationBlocked] = useState<number | null>(null)

  /**
   * 저장 시점에 계산된 검증 결과와, 그때의 행 스냅샷.
   *
   * @why 행 배열을 함께 들고 있는 이유: 사용자가 한 글자라도 고치면 rows는
   * 새 배열이 되므로, 참조만 비교해도 "저장 시점 이후 바뀌었는지"를 정확히 알 수 있다.
   * 별도 플래그나 useEffect 없이 자동으로 최신 검증으로 돌아간다.
   */
  const [saveSummary, setSaveSummary] = useState<{
    rows: BulkTransactionDraftRow[]
    summary: BulkValidationSummary
  } | null>(null)

  /**
   * 저장 시도 후 확정된 검증 결과.
   *
   * @why 입력하자마자 빨간 오류를 뿌리면 아직 채우는 중인 칸까지 잘못된 것처럼 보인다.
   * 저장을 누른 뒤부터 오류를 보여주고, 그 뒤로는 고칠 때마다 즉시 갱신한다.
   */
  const [showErrors, setShowErrors] = useState(false)

  const validationContext = useMemo<BulkValidationContext>(
    () => ({
      categories: [...expenseCategories, ...incomeCategories],
      paymentMethods,
      users,
    }),
    [expenseCategories, incomeCategories, paymentMethods, users]
  )

  /** 화면이 처음 읽어둔 마스터로 계산한 검증 결과 */
  const liveSummary: BulkValidationSummary = useMemo(
    () => validateAll(rows, validationContext),
    [rows, validationContext]
  )

  /**
   * 실제로 화면에 표시할 검증 결과.
   *
   * @why 저장을 막은 판정은 "저장 직전에 다시 읽은 마스터"로 계산한 것이라
   * 화면의 liveSummary와 다를 수 있다. 그 경우 저장을 막은 근거를 그대로 보여줘야
   * 사용자가 어느 행을 고쳐야 할지 알 수 있다.
   * (근거를 안 보여줘서 "빨간 표시는 없는데 저장이 안 되는" 버그가 났었다)
   *
   * 행이 하나라도 바뀌면 rows 참조가 새로 만들어지므로 자동으로 liveSummary로 돌아간다.
   * 사용자가 고치는 즉시 화면이 반응해야 하기 때문에, 저장 시점 결과를 붙들고 있으면 안 된다.
   */
  const summary: BulkValidationSummary =
    saveSummary?.rows === rows ? saveSummary.summary : liveSummary

  const paymentMethodOptions = useMemo(
    () => paymentMethods.map((pm) => ({ value: pm.id, label: pm.name })),
    [paymentMethods]
  )

  const userOptions = useMemo(
    () => users.map((user) => ({ value: user.id, label: user.name })),
    [users]
  )

  /**
   * 행 표현에 넘길 props 조립
   *
   * @why 테이블과 카드가 같은 계약을 쓰므로 조립을 한 곳에서 한다.
   */
  const buildRowProps = useCallback(
    (row: BulkTransactionDraftRow, index: number): BulkRowViewProps => ({
      row,
      rowNumber: index + 1,
      validation: showErrors ? summary.byClientId[row.clientId] : undefined,
      expenseCategories,
      incomeCategories,
      paymentMethodOptions,
      userOptions,
      onChange: updateRow,
      onRemove: removeRow,
      disabled: saving,
    }),
    [
      showErrors,
      summary,
      expenseCategories,
      incomeCategories,
      paymentMethodOptions,
      userOptions,
      updateRow,
      removeRow,
      saving,
    ]
  )

  const handleSave = async () => {
    setShowErrors(true)
    const result = await saveAll({
      rows,
      // 저장 직전 재검증에 쓸 마스터 데이터.
      // 어떤 질의를 쓰는지가 검증 결과를 좌우하므로 로더를 분리해 테스트로 고정했다.
      // (자세한 배경은 loadBulkValidationContext의 주석 참조)
      refreshContext: loadBulkValidationContext,
    })

    /**
     * 저장 시점 검증 결과를 화면에 반영한다.
     *
     * @why 화면의 summary는 페이지가 처음 읽어둔 마스터로 계산한 것이고,
     * 저장을 막은 판정은 방금 다시 읽은 마스터로 계산한 것이다.
     * 둘이 어긋나면 "오류가 N건 있다"는 안내만 뜨고 정작 빨간 표시는
     * 어디에도 없는 상태가 된다. 실제로 그 버그가 났었다.
     * 저장을 막은 근거를 그대로 화면에 보여줘야 사용자가 고칠 수 있다.
     */
    if (result.summary) {
      setSaveSummary({ rows, summary: result.summary })
    }

    // 저장에 성공했을 때만 보관 내용을 비운다.
    // 실패한 경우까지 비우면 사용자가 방금 입력한 내용을 통째로 잃는다.
    if (result.ok) {
      clearDraft()
      return
    }

    /**
     * 실패를 모달로 알린다.
     *
     * @why 저장 버튼은 화면 하단에 고정돼 있고 내용은 길게 스크롤된다.
     * 실패를 페이지 상단 배너로만 그리면, 아래쪽을 보고 있던 사용자에게는
     * 화면 밖에서 벌어진 일이라 "눌러도 아무 일이 없다"로 보인다.
     * (태블릿에서 저장이 안 된다는 신고의 실제 체감이 이것이었다)
     */
    if (result.failure) {
      setFailureDialog(result.failure)
      return
    }

    // 요청 실패가 아니라 검증으로 막힌 경우. 어느 행인지는 각 행에 표시되지만,
    // 그 행이 화면 밖일 수 있으므로 막혔다는 사실 자체를 먼저 알린다.
    if (result.summary && !result.summary.canSave) {
      setValidationBlocked(result.summary.errorCount)
    }
  }

  const rowCountWarning = rows.length > ROW_COUNT_WARNING_THRESHOLD

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      <header className="sticky top-0 z-10 bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 px-4 py-3 flex items-center justify-between gap-3">
        <div>
          <h1 className="text-xl font-bold text-gray-900 dark:text-gray-100">
            거래 일괄 입력
          </h1>
          <p className="text-sm text-gray-500 dark:text-gray-400">
            여러 건을 입력한 뒤 마지막에 한 번만 저장합니다
          </p>
        </div>
        <Button type="button" variant="secondary" onClick={() => navigate('/register')}>
          단건 입력
        </Button>
      </header>

      <div className="px-4 py-4 space-y-4 pb-40">
        {isDataLoading && (
          <p className="text-center py-4 text-gray-500 dark:text-gray-400">
            데이터를 불러오는 중...
          </p>
        )}

        {rowCountWarning && (
          <p className="text-sm text-amber-700 dark:text-amber-400">
            행이 {rows.length}개입니다. 한 번에 {DEFAULT_MAX_ROWS}행까지 입력할 수 있어요.
          </p>
        )}

        {isWideScreen ? (
          <BulkTransactionTable rows={rows} renderProps={buildRowProps} />
        ) : (
          <ul className="space-y-3">
            {rows.map((row, index) => (
              // key는 배열 인덱스가 아닌 clientId (행 삭제 시 포커스·IME 상태 보존)
              <BulkTransactionRowCard key={row.clientId} {...buildRowProps(row, index)} />
            ))}
          </ul>
        )}

        <Button
          type="button"
          variant="secondary"
          onClick={addRow}
          disabled={!canAddRow || saving}
          fullWidth
        >
          + 행 추가
        </Button>
      </div>

      {/*
        저장 바: 스크롤과 무관하게 항상 닿을 수 있도록 하단 고정.
        단, 하단 탭 내비게이션(fixed bottom-0, z-50, 높이 약 5rem)이 이미 자리를 차지하므로
        bottom-20으로 그 위에 얹고 z-40을 줘서 내비게이션 아래 층에 놓는다.
        (bottom-0로 두면 내비게이션에 가려 저장 버튼 클릭이 탭 이동으로 새어 나간다)
      */}
      <div className="fixed bottom-20 left-0 right-0 z-40 border-t border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 px-4 py-3">
        <div className="max-w-4xl mx-auto flex items-center gap-3">
          <p className="text-sm text-gray-600 dark:text-gray-300 flex-1">
            저장 대상 <strong>{summary.validCount}</strong>건
            {summary.errorCount > 0 && (
              <span className="text-red-600 dark:text-red-400">
                {' '}
                · 오류 {summary.errorCount}건
              </span>
            )}
          </p>
          <Button
            type="button"
            variant="primary"
            onClick={handleSave}
            loading={saving}
            disabled={saving || summary.validCount === 0}
          >
            전체 저장
          </Button>
        </div>
      </div>

      {/*
        저장 실패 알림.
        stage에 따라 안내가 갈린다 — 재검증(읽기)에서 끊겼다면 저장은 시도조차
        되지 않았으므로 그냥 다시 누르면 되고, 저장(쓰기)에서 끊겼다면
        이미 반영됐을 수 있어 목록 확인이 먼저다.
      */}
      <ErrorDialog
        isOpen={failureDialog !== null}
        onClose={() => setFailureDialog(null)}
        title="거래 저장 실패"
        message={
          failureDialog?.stage === 'refresh'
            ? `저장 전 최신 정보를 불러오지 못했습니다.\n\n${failureDialog.message}`
            : (failureDialog?.message ?? '')
        }
        warning={
          failureDialog?.mayHaveSaved
            ? '저장이 이미 반영되었을 수 있습니다. 다시 저장하기 전에 거래 목록을 확인해주세요.'
            : undefined
        }
        {...(failureDialog?.detail ? { details: failureDialog.detail } : {})}
        onRetry={() => {
          setFailureDialog(null)
          void handleSave()
        }}
      />

      {/*
        검증으로 막힌 경우.
        어느 행이 문제인지는 각 행에 이미 표시되지만 화면 밖일 수 있으므로,
        "막혔다"는 사실과 건수를 먼저 알린다.
      */}
      <ErrorDialog
        isOpen={validationBlocked !== null}
        onClose={() => setValidationBlocked(null)}
        title="저장할 수 없습니다"
        message={`입력에 오류가 있는 행이 ${validationBlocked ?? 0}건 있습니다.\n빨간색으로 표시된 행을 수정한 뒤 다시 저장해주세요.`}
      />
    </div>
  )
}
